/**
 * /reports/charts — AI Chart Builder.
 *
 * User types a plain-English prompt; we call the existing /api/reports/build
 * endpoint to get live GL data, then render it as a bar, line, pie, or donut
 * chart as crisp SVG via Recharts. Export to PNG / JPEG (rasterized on
 * demand) or CSV (opens natively in Excel / Numbers).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState, useRef, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Brush,
} from "recharts";
import { api, errorMessage, errorDetail } from "@/lib/api";
import type { BuildResponse } from "@/lib/queries";

export const Route = createFileRoute("/_auth/reports/charts")({
  component: ChartBuilderPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type ChartType = "bar" | "line" | "pie" | "donut";

interface ChartDataset {
  label: string;
  values: number[];
  color: string;
}

interface ChartData {
  title: string;
  labels: string[];        // x-axis / slice labels (original, untidied)
  labelKey: string;        // source column the labels came from (for drill-down filtering)
  datasets: ChartDataset[];
  explanation: string;
  rawRows: Record<string, unknown>[];
  rawColumns: { name: string; type: string }[];
  stacked?: boolean;       // true when bars should stack (series-derived buckets)
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CHART_PALETTE = [
  "#4f46e5", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
  "#f97316", "#6366f1",
];

const STARTER_PROMPTS = [
  { label: "Revenue by project — bar chart", prompt: "Show total billed amount by project this year as a bar chart" },
  { label: "Monthly labor cost trend", prompt: "Monthly labor cost trend for the last 6 months as a line chart" },
  { label: "Cost breakdown by category", prompt: "Break down total costs by cost code category as a pie chart" },
  { label: "AR aging by customer", prompt: "Outstanding AR balance by customer as a bar chart" },
  { label: "Budget vs actual — top projects", prompt: "Budget vs actual spend for our top 5 projects side by side" },
  { label: "Payroll by trade this month", prompt: "Total payroll cost by trade classification this month as a donut chart" },
];

const CHART_TYPE_META: { type: ChartType; label: string; emoji: string }[] = [
  { type: "bar",   label: "Bar",   emoji: "▊" },
  { type: "line",  label: "Line",  emoji: "📈" },
  { type: "pie",   label: "Pie",   emoji: "🥧" },
  { type: "donut", label: "Donut", emoji: "⭕" },
];

// ── Chart rendering (Recharts / SVG — crisp at any DPI) ─────────────────

const AXIS_TICK = { fontSize: 12, fill: "#64748b" };

/** Abbreviate large numbers for axes/tooltips: 236000 -> "236K". */
function fmtNum(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + "K";
  return String(Math.round(n));
}

/** Tidy long / ISO-date axis labels: "2026-04-01T00:00:00" -> "Apr 2026". */
function tidyLabel(v: unknown): string {
  const str = String(v ?? "");
  const m = str.match(/^(\d{4})-(\d{2})-\d{2}/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  }
  return str.length > 16 ? str.slice(0, 15) + "\u2026" : str;
}

function ChartView({
  data,
  type,
  onDrill,
}: {
  data: ChartData;
  type: ChartType;
  onDrill?: (rawLabel: string) => void;
}) {
  // Recharts onClick gives the category index; map it back to the ORIGINAL
  // (untidied) label so the page can filter the underlying rows.
  const clickAt = onDrill ? (_: any, index: number) => onDrill(data.labels[index]) : undefined;
  const cursor = onDrill ? "pointer" : undefined;

  if (type === "pie" || type === "donut") {
    const ds = data.datasets[0];
    const pieData = data.labels.map((label, i) => ({
      name: tidyLabel(label),
      value: ds?.values[i] ?? 0,
    }));
    return (
      <ResponsiveContainer width="100%" height={440}>
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={150}
            innerRadius={type === "donut" ? 90 : 0}
            paddingAngle={1}
            labelLine={false}
            label={(e: any) => `${e.name}: ${fmtNum(Number(e.value))}`}
            onClick={clickAt}
            cursor={cursor}
          >
            {pieData.map((_, i) => (
              <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v: any) => fmtNum(Number(v))} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // bar / line — one series per dataset, aligned on the shared labels
  const rows = data.labels.map((label, i) => {
    const row: Record<string, number | string> = { name: tidyLabel(label) };
    data.datasets.forEach((ds) => {
      row[ds.label] = ds.values[i] ?? 0;
    });
    return row;
  });

  // Data-range zoom slider — only worth showing once there are many points.
  const brush =
    rows.length > 8 ? (
      <Brush dataKey="name" height={22} travellerWidth={8} stroke="#94a3b8" fill="transparent" />
    ) : null;

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
      <XAxis dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} interval="preserveStartEnd" />
      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={fmtNum} width={60} />
      <Tooltip formatter={(v: any) => fmtNum(Number(v))} cursor={{ fill: "#f1f5f9" }} />
      <Legend />
    </>
  );

  return (
    <ResponsiveContainer width="100%" height={brush ? 480 : 440}>
      {type === "line" ? (
        <LineChart data={rows} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
          {axes}
          {data.datasets.map((ds) => (
            <Line key={ds.label} type="monotone" dataKey={ds.label} stroke={ds.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          ))}
          {brush}
        </LineChart>
      ) : (
        <BarChart data={rows} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
          {axes}
          {data.datasets.map((ds) => (
            <Bar key={ds.label} dataKey={ds.label} fill={ds.color} radius={data.stacked ? undefined : [4, 4, 0, 0]} maxBarSize={64} stackId={data.stacked ? "a" : undefined} onClick={clickAt} cursor={cursor} />
          ))}
          {brush}
        </BarChart>
      )}
    </ResponsiveContainer>
  );
}

// ── Data transformation ────────────────────────────────────────────────────────

function guessChartType(prompt: string): ChartType {
  const p = prompt.toLowerCase();
  if (p.includes("pie"))   return "pie";
  if (p.includes("donut")) return "donut";
  if (p.includes("line") || p.includes("trend") || p.includes("over time") || p.includes("monthly") || p.includes("weekly")) return "line";
  return "bar";
}

/** Match a chart-spec field name (e.g. "Open Amount" / "open_amount") to a column. */
function resolveCol(name: string | undefined, columns: { name: string; type: string }[]): string | undefined {
  if (!name) return undefined;
  const lower = name.toLowerCase();
  const snake = name.replace(/ /g, "_").toLowerCase();
  return columns.find(
    (c) => c.name === name || c.name.toLowerCase() === lower || c.name.replace(/ /g, "_").toLowerCase() === snake,
  )?.name;
}

function num(v: unknown): number {
  return typeof v === "number" ? v : parseFloat(String(v ?? "0")) || 0;
}

function rowValue(row: Record<string, unknown>, column: string): unknown {
  const normalized = column.replace(/ /g, "_").replace(/\//g, "_");
  return row[column] ?? row[normalized];
}

function rowsToChartData(
  rows: Record<string, unknown>[],
  columns: { name: string; type: string }[],
  title: string,
  explanation: string,
  chart?: { x?: string; y?: string; series?: string } | null,
): ChartData {
  // Preferred path: honor the AI's chart spec (x / y / series). The DSL often
  // returns DETAIL rows, so we aggregate y by x (split by series) ourselves —
  // this is what makes "AR aging by month" actually plot by month, not by the
  // first stringy column the heuristic happens to find.
  const xKey = resolveCol(chart?.x, columns);
  const yKey = resolveCol(chart?.y, columns);
  const seriesKey = resolveCol(chart?.series, columns);

  if (xKey && yKey) {
    const xVals: string[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
      const xv = String(rowValue(r, xKey) ?? "—");
      if (!seen.has(xv)) { seen.add(xv); xVals.push(xv); }
    }
    xVals.sort(); // ISO dates sort chronologically; plain strings alphabetically
    const labels = xVals.slice(0, 60);

    let datasets: ChartDataset[];
    if (seriesKey) {
      const sVals: string[] = [];
      const sSeen = new Set<string>();
      for (const r of rows) {
        const sv = String(rowValue(r, seriesKey) ?? "—");
        if (!sSeen.has(sv)) { sSeen.add(sv); sVals.push(sv); }
      }
      datasets = sVals.map((sv, i) => ({
        label: sv,
        color: CHART_PALETTE[i % CHART_PALETTE.length],
        values: labels.map((xv) =>
          rows.reduce(
            (acc, r) =>
              String(rowValue(r, xKey) ?? "—") === xv && String(rowValue(r, seriesKey) ?? "—") === sv
                ? acc + num(rowValue(r, yKey))
                : acc,
            0,
          ),
        ),
      }));
    } else {
      datasets = [{
        label: yKey,
        color: CHART_PALETTE[0],
        values: labels.map((xv) => rows.reduce(
          (acc, r) => String(rowValue(r, xKey) ?? "—") === xv
            ? acc + num(rowValue(r, yKey))
            : acc,
          0,
        )),
      }];
    }
    return { title, labels, labelKey: xKey, datasets, explanation, rawRows: rows, rawColumns: columns, stacked: !!seriesKey };
  }

  // Fallback heuristic: first non-numeric column = label, numeric columns = series.
  // Backend emits "int" (not "integer") and sometimes "float"/"number" — accept all.
  const NUMERIC = new Set(["currency", "number", "integer", "int", "float", "decimal"]);
  const labelCols = columns.filter((c) => !NUMERIC.has(c.type));
  const measureCols = columns.filter((c) => NUMERIC.has(c.type));
  const effectiveLabelCols = labelCols.length > 0 ? labelCols : [columns[0]].filter(Boolean);
  const effectiveMeasureCols =
    measureCols.length > 0
      ? measureCols
      : columns.slice(1).filter((c) => {
          const sample = rows[0] ? rowValue(rows[0], c.name) : undefined;
          return typeof sample === "number" || (typeof sample === "string" && !isNaN(Number(sample)));
        });

  const labelKey = effectiveLabelCols[0]?.name ?? columns[0]?.name ?? "label";
  const labels = rows.map((r) => String(rowValue(r, labelKey) ?? "—")).slice(0, 20);
  const datasets: ChartDataset[] = effectiveMeasureCols.slice(0, 5).map((col, i) => ({
    label: col.name,
    color: CHART_PALETTE[i % CHART_PALETTE.length],
    values: rows.map((r) => num(rowValue(r, col.name))).slice(0, 20),
  }));
  return { title, labels, labelKey, datasets, explanation, rawRows: rows, rawColumns: columns, stacked: false };
}

function buildCsv(data: ChartData): string {
  const header = [data.rawColumns.map((c) => `"${c.name}"`).join(",")];
  const dataRows = data.rawRows.map((row) =>
    data.rawColumns
      .map((c) => {
        const v = row[c.name] ?? row[c.name.replace(/ /g, "_")];
        return typeof v === "string" ? `"${v.replace(/"/g, '""')}"` : String(v ?? "");
      })
      .join(",")
  );
  return [...header, ...dataRows].join("\n");
}

// ── Page ───────────────────────────────────────────────────────────────────────

function ChartBuilderPage() {
  const [prompt, setPrompt] = useState("");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [history, setHistory] = useState<{
    prompt: string;
    data: ChartData;
    type: ChartType;
    response: BuildResponse;
  }[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [drillEnabled, setDrillEnabled] = useState(true);
  const [drill, setDrill] = useState<{ label: string; rows: Record<string, unknown>[] } | null>(null);

  // Click-to-drill: filter the underlying rows to the clicked category.
  const handleDrill = (rawLabel: string) => {
    if (!chartData) return;
    const key = chartData.labelKey;
    const matches = chartData.rawRows.filter(
      (r) => String(rowValue(r, key) ?? "") === String(rawLabel),
    );
    setDrill({ label: String(rawLabel), rows: matches });
  };

  // Non-fatal response states that still need to surface to the user.
  // None of these throw — we render them inline instead of the chart.
  const [lastResponse, setLastResponse] = useState<BuildResponse | null>(null);

  const applyResponse = useCallback((res: BuildResponse, requestPrompt: string) => {
    setLastResponse(res);
    setDrill(null);

    if (
      (res.clarifying_questions && res.clarifying_questions.length > 0)
      || (res.schema_warnings && res.schema_warnings.length > 0)
      || !res.preview_rows
      || res.preview_rows.length === 0
      || !res.columns
      || res.columns.length === 0
    ) {
      setChartData(null);
      return;
    }

    const dsl = res.dsl as Record<string, unknown>;
    const title = (dsl?.title as string) || requestPrompt.slice(0, 60);
    const chartSpec = (dsl?.viz as {
      chart?: { x?: string; y?: string; series?: string; type?: string };
    } | undefined)?.chart;
    const data = rowsToChartData(
      res.preview_rows,
      res.columns,
      title,
      res.ai_explanation ?? "",
      chartSpec,
    );
    const aiType = chartSpec?.type;
    const guessedFromPrompt = guessChartType(requestPrompt);
    const selectedType: ChartType = guessedFromPrompt === "donut"
      ? "donut"
      : aiType === "line" ? "line"
        : aiType === "pie" ? "pie"
          : aiType === "bar" || aiType === "stacked_bar" || aiType === "combo" ? "bar"
            : guessedFromPrompt;
    setChartType(selectedType);
    setChartData(data);
    setHistory((h) => [
      { prompt: requestPrompt, data, type: selectedType, response: res },
      ...h,
    ].slice(0, 8));
  }, []);

  const buildMutation = useMutation({
    mutationFn: (p: string) =>
      api<BuildResponse>("/api/reports/build", {
        method: "POST",
        body: JSON.stringify({ prompt: p, context: {}, answers: {} }),
      }),
    onSuccess: (res, p) => applyResponse(res, p),
  });

  const refineMutation = useMutation({
    mutationFn: ({ instruction, currentDsl }: {
      instruction: string;
      currentDsl: Record<string, unknown>;
    }) => api<BuildResponse>("/api/reports/build/refine", {
      method: "POST",
      body: JSON.stringify({ current_dsl: currentDsl, instruction }),
    }),
    onSuccess: (res, vars) => {
      const refinedPrompt = `${prompt.trim()} — ${vars.instruction}`;
      setPrompt(refinedPrompt);
      applyResponse(res, refinedPrompt);
    },
  });

  const submit = useCallback((p: string) => {
    const t = p.trim();
    if (!t) return;
    setLastResponse(null); // clear stale warnings/clarifications from previous build
    buildMutation.mutate(t);
  }, [buildMutation]);

  // Rasterize the rendered SVG on demand: serialize -> draw onto a canvas at
  // devicePixelRatio for a crisp PNG/JPEG, compositing the title + a legend
  // (the live legend is HTML, so it isn't part of the SVG we serialize).
  const exportAs = (format: "png" | "jpeg") => {
    const svg = chartRef.current?.querySelector("svg");
    if (!svg || !chartData) return;
    const rect = svg.getBoundingClientRect();
    const cw = Math.max(1, Math.ceil(rect.width));
    const ch = Math.max(1, Math.ceil(rect.height));
    const dpr = Math.max(2, window.devicePixelRatio || 1);
    const titleH = 44;
    const legendH = 40;
    const pad = 20;
    const totalH = titleH + ch + legendH;

    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", String(cw));
    clone.setAttribute("height", String(ch));
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("font-family", "ui-sans-serif, system-ui, -apple-system, sans-serif");
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = cw * dpr;
      canvas.height = totalH * dpr;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, cw, totalH);
      ctx.fillStyle = "#0f172a";
      ctx.font = "600 16px ui-sans-serif, system-ui, -apple-system, sans-serif";
      ctx.fillText(chartData.title, pad, 28);
      ctx.drawImage(img, 0, titleH, cw, ch);
      const items =
        chartType === "pie" || chartType === "donut"
          ? chartData.labels.map((l, i) => ({ label: l, color: CHART_PALETTE[i % CHART_PALETTE.length] }))
          : chartData.datasets.map((ds) => ({ label: ds.label, color: ds.color }));
      ctx.font = "12px ui-sans-serif, system-ui, -apple-system, sans-serif";
      let x = pad;
      const y = titleH + ch + 24;
      for (const it of items.slice(0, 10)) {
        ctx.fillStyle = it.color;
        ctx.fillRect(x, y - 9, 11, 11);
        ctx.fillStyle = "#475569";
        ctx.fillText(String(it.label), x + 16, y);
        x += 16 + ctx.measureText(String(it.label)).width + 20;
      }
      const url = canvas.toDataURL(format === "jpeg" ? "image/jpeg" : "image/png", 0.95);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${chartData.title.replace(/[^a-z0-9]/gi, "_")}.${format}`;
      a.click();
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
  };

  const exportCsv = () => {
    if (!chartData) return;
    const csv = buildCsv(chartData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${chartData.title.replace(/[^a-z0-9]/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isPending = buildMutation.isPending || refineMutation.isPending;
  const error = buildMutation.error || refineMutation.error;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-slate-50 dark:bg-gray-950">

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-3 mb-0.5">
          <Link
            to="/reports"
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-sm"
          >
            ← Reports
          </Link>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span>📊</span> AI Chart Builder
          </h1>
          <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full font-medium">
            ✨ Powered by AI
          </span>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Describe a chart in plain English — AI builds it from your live GL data. Export as PNG, JPEG, or Excel.
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── Left panel: prompt + controls ── */}
        <div className="w-[340px] flex-shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">

          {/* Prompt area */}
          <div className="p-4 border-b border-slate-100 dark:border-gray-800 flex-shrink-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
              Describe your chart
            </label>
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && prompt.trim()) {
                  e.preventDefault();
                  submit(prompt);
                }
              }}
              placeholder="e.g. Show me revenue by project this year as a bar chart…"
              rows={4}
              className="w-full text-sm border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-slate-400">⌘↵ to generate</span>
              <button
                onClick={() => submit(prompt)}
                disabled={!prompt.trim() || isPending}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-lg text-white transition disabled:opacity-50"
                style={{ background: "#4f46e5" }}
                onMouseOver={(e) => { if (!isPending) (e.currentTarget as HTMLElement).style.background = "#4338ca"; }}
                onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.background = "#4f46e5"; }}
              >
                {isPending ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Building…
                  </>
                ) : (
                  <><span>✦</span> Generate</>
                )}
              </button>
            </div>
          </div>

          {/* Chart type switcher */}
          {chartData && (
            <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex-shrink-0">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Chart type
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {CHART_TYPE_META.map(({ type, label, emoji }) => (
                  <button
                    key={type}
                    onClick={() => setChartType(type)}
                    className={`flex flex-col items-center gap-0.5 py-2 rounded-lg border text-xs font-medium transition ${
                      chartType === type
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                        : "border-slate-200 dark:border-gray-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-gray-600"
                    }`}
                  >
                    <span className="text-lg">{emoji}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Interactivity */}
          {chartData && (
            <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex-shrink-0">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Interactivity
              </div>
              <label className="flex items-center justify-between cursor-pointer select-none">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  Click to drill in
                  <span className="block text-[11px] text-slate-400 dark:text-slate-500">Click a bar / slice to see its rows</span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={drillEnabled}
                  onClick={() => { setDrillEnabled((v) => !v); if (drillEnabled) setDrill(null); }}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition ${drillEnabled ? "bg-indigo-500" : "bg-slate-300 dark:bg-gray-600"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${drillEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </label>
            </div>
          )}

          {/* Export buttons */}
          {chartData && (
            <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex-shrink-0">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                Export
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => exportAs("png")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
                >
                  <span>🖼️</span>
                  <span>Download PNG</span>
                  <span className="ml-auto text-xs text-slate-400">Lossless</span>
                </button>
                <button
                  onClick={() => exportAs("jpeg")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
                >
                  <span>📷</span>
                  <span>Download JPEG</span>
                  <span className="ml-auto text-xs text-slate-400">Compressed</span>
                </button>
                <button
                  onClick={exportCsv}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-800 text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition"
                >
                  <span>📊</span>
                  <span>Export to Excel / CSV</span>
                </button>
              </div>
            </div>
          )}

          {/* Starter prompts */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Example charts
            </div>
            <div className="flex flex-col gap-1.5">
              {STARTER_PROMPTS.map((sp) => (
                <button
                  key={sp.prompt}
                  onClick={() => {
                    setPrompt(sp.prompt);
                    submit(sp.prompt);
                  }}
                  className="text-left px-3 py-2 rounded-lg text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-gray-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-700 dark:hover:text-indigo-300 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800 transition"
                >
                  {sp.label}
                </button>
              ))}
            </div>

            {/* History */}
            {history.length > 1 && (
              <>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-5 mb-2">
                  Recent charts
                </div>
                <div className="flex flex-col gap-1.5">
                  {history.slice(1).map((h, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setPrompt(h.prompt);
                        setChartData(h.data);
                        setChartType(h.type);
                        setLastResponse(h.response);
                      }}
                      className="text-left px-3 py-2 rounded-lg text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800 transition truncate"
                    >
                      {h.prompt.length > 48 ? h.prompt.slice(0, 47) + "…" : h.prompt}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Right panel: canvas ── */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error — surface the server-side detail with actionable deep links */}
          {error && (() => {
            const struct = errorDetail(error);
            return (
              <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0">⚠️</span>
                  <div className="flex-1">
                    <div>{errorMessage(error)}</div>
                    {struct?.action || struct?.external ? (
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {struct?.action && (
                          <Link
                            to={struct.action.href}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition"
                          >
                            {struct.action.label} →
                          </Link>
                        )}
                        {struct?.external && (
                          <a
                            href={struct.external.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 text-xs font-medium transition"
                          >
                            {struct.external.label} ↗
                          </a>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Non-fatal response — the build returned 200 but produced no chart:
              the AI asked clarifying questions, the query ran but matched zero
              rows, or schema_warnings flagged an issue (e.g. invalid enum). */}
          {!chartData && !isPending && lastResponse && (
            (lastResponse.clarifying_questions?.length ?? 0) > 0
            || (lastResponse.schema_warnings?.length ?? 0) > 0
            || (lastResponse.preview_rows?.length ?? 0) === 0
          ) && (
            <div className="mb-4 bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-900/40 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/40 flex items-center gap-2">
                <span className="text-amber-600">⚠️</span>
                <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  {(lastResponse.clarifying_questions?.length ?? 0) > 0
                    ? "I need more detail to build this"
                    : (lastResponse.schema_warnings?.length ?? 0) > 0
                    ? "The query couldn't run"
                    : "No matching data"}
                </span>
              </div>
              <div className="p-5 space-y-3 text-sm">
                {/* Clarifying questions — show as bullet list */}
                {(lastResponse.clarifying_questions?.length ?? 0) > 0 && (
                  <div>
                    <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                      {lastResponse.clarifying_questions.map((q) => (
                        <li key={q.id}>{q.question}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                      Add the answers into your prompt and click Generate again.
                    </p>
                  </div>
                )}

                {/* Schema warnings — show as red preformatted block (typically a
                    SQL error from the executor) */}
                {(lastResponse.schema_warnings?.length ?? 0) > 0 && (
                  <div className="space-y-2">
                    {lastResponse.schema_warnings.map((w, i) => (
                      <details
                        key={i}
                        className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 overflow-hidden"
                      >
                        <summary className="px-3 py-2 text-xs font-medium text-red-800 dark:text-red-300 cursor-pointer hover:bg-red-100 dark:hover:bg-red-950/40">
                          {w.length > 140 ? w.slice(0, 140) + "…" : w}
                        </summary>
                        <pre className="px-3 py-2 text-[11px] text-red-700 dark:text-red-300 whitespace-pre-wrap font-mono border-t border-red-200 dark:border-red-900/40 bg-white/50 dark:bg-gray-950/50">
                          {w}
                        </pre>
                      </details>
                    ))}
                    <p className="text-xs text-slate-500 dark:text-slate-500">
                      The AI's query couldn't execute. Try refining the prompt — e.g. specify
                      a status ("open", "paid") or a date window.
                    </p>
                  </div>
                )}

                {/* Empty result set */}
                {(lastResponse.clarifying_questions?.length ?? 0) === 0
                  && (lastResponse.schema_warnings?.length ?? 0) === 0
                  && (lastResponse.preview_rows?.length ?? 0) === 0 && (
                  <p className="text-slate-700 dark:text-slate-300">
                    Query ran successfully but matched zero rows. Try broadening the filters
                    (different date range, remove status filter, etc.).
                  </p>
                )}

                {/* AI explanation — always useful context */}
                {lastResponse.ai_explanation && (
                  <details className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <summary className="cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
                      What the AI tried to build
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap leading-relaxed">
                      {lastResponse.ai_explanation}
                    </p>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!chartData && !isPending && !lastResponse && (
            <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 dark:text-slate-600 gap-4">
              <div className="text-7xl opacity-40">📊</div>
              <div>
                <p className="text-lg font-semibold text-slate-500 dark:text-slate-400 mb-1">No chart yet</p>
                <p className="text-sm">Type a prompt on the left or pick an example to get started.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 max-w-lg">
                {STARTER_PROMPTS.slice(0, 4).map((sp) => (
                  <button
                    key={sp.prompt}
                    onClick={() => { setPrompt(sp.prompt); submit(sp.prompt); }}
                    className="text-left px-4 py-3 rounded-xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 text-sm text-slate-600 dark:text-slate-300 hover:border-indigo-400 dark:hover:border-indigo-600 hover:shadow-sm transition"
                  >
                    {sp.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading skeleton */}
          {isPending && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 overflow-hidden animate-pulse">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800">
                <div className="h-5 w-64 bg-slate-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-48 bg-slate-100 dark:bg-gray-800 rounded mt-2" />
              </div>
              <div className="p-6">
                <div className="flex items-end gap-4 h-64">
                  {[55, 78, 42, 91, 63, 87, 49].map((h, i) => (
                    <div key={i} className="flex-1 bg-slate-100 dark:bg-gray-800 rounded-t-md" style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="mt-4 flex gap-3 justify-center">
                  {[60, 80, 70].map((w, i) => (
                    <div key={i} className="h-3 bg-slate-100 dark:bg-gray-800 rounded" style={{ width: `${w}px` }} />
                  ))}
                </div>
              </div>
              <div className="px-6 pb-5 flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-slate-500 dark:text-slate-400">AI is building your chart…</span>
              </div>
            </div>
          )}

          {/* Chart canvas */}
          {chartData && !isPending && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 overflow-hidden shadow-sm">
              {/* Chart header */}
              <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{chartData.title}</h2>
                  {chartData.explanation && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug max-w-xl">
                      {chartData.explanation}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {chartData.rawRows.length} rows · live GL data
                  </span>
                  <span className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 px-2 py-0.5 rounded-full">
                    ✨ AI built
                  </span>
                </div>
              </div>

              {/* Chart (crisp SVG) */}
              <div ref={chartRef} className="p-4">
                <ChartView data={chartData} type={chartType} onDrill={drillEnabled ? handleDrill : undefined} />
              </div>

              {/* Drill-in panel — rows behind the clicked category */}
              {drill && (
                <div className="px-4 pb-4">
                  <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/40 dark:bg-indigo-900/10 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-indigo-100 dark:border-indigo-900/40">
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                        🔎 Drilled into <span className="font-semibold">{drill.label}</span> · {drill.rows.length} {drill.rows.length === 1 ? "row" : "rows"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setDrill(null)}
                        className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        ✕ close
                      </button>
                    </div>
                    <div className="overflow-x-auto max-h-72">
                      <table className="w-full text-xs">
                        <thead className="bg-white/70 dark:bg-gray-900/40 sticky top-0">
                          <tr>
                            {chartData.rawColumns.map((c) => (
                              <th key={c.name} className="text-left font-medium text-slate-500 dark:text-slate-400 px-3 py-2 whitespace-nowrap">{c.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {drill.rows.map((r, ri) => (
                            <tr key={ri} className="border-t border-indigo-100/60 dark:border-indigo-900/30">
                              {chartData.rawColumns.map((c) => (
                                <td key={c.name} className="px-3 py-1.5 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                  {String(r[c.name] ?? r[c.name.replace(/ /g, "_")] ?? "")}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Refine bar */}
              <div className="px-6 py-3 border-t border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-950/50">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">Refine:</span>
                  <input
                    placeholder="e.g. only show top 5 projects, or change to a line chart…"
                    className="flex-1 text-sm border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const val = (e.target as HTMLInputElement).value.trim();
                        if (val && lastResponse?.dsl) {
                          refineMutation.mutate({ instruction: val, currentDsl: lastResponse.dsl });
                          (e.target as HTMLInputElement).value = "";
                        }
                      }
                    }}
                  />
                  <span className="text-[10px] text-slate-400 flex-shrink-0">↵ to update</span>
                </div>
              </div>
            </div>
          )}

          {/* Data table (collapsed) */}
          {chartData && !isPending && chartData.rawRows.length > 0 && (
            <details className="mt-4 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl overflow-hidden">
              <summary className="px-5 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-800 transition select-none">
                📋 View underlying data ({chartData.rawRows.length} rows)
              </summary>
              <div className="overflow-x-auto border-t border-slate-100 dark:border-gray-800">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-gray-900">
                    <tr>
                      {chartData.rawColumns.map((c) => (
                        <th
                          key={c.name}
                          className="px-4 py-2 text-left font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                        >
                          {c.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.rawRows.slice(0, 50).map((row, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800">
                        {chartData.rawColumns.map((c) => {
                          const v = row[c.name] ?? row[c.name.replace(/ /g, "_")];
                          const fmt =
                            c.type === "currency" && typeof v === "number"
                              ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v)
                              : String(v ?? "—");
                          return (
                            <td key={c.name} className="px-4 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                              {fmt}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
